import { isPublicProfileSectionRenderable } from "./publicProfileSectionGating";

export type ExperienceHeatLevel = "top_10" | "top_25" | "top_50" | "bottom_50" | "none";

export type MissionHeatmapBucketInput = Readonly<{
  bucketKey: string;
  missionCount: number;
}>;

export type MissionHeatmapLocationInput = Readonly<{
  locationKey: string;
  cityLabel: string;
  latitude: number;
  longitude: number;
  missionCount: number;
  portfolioPercentage?: number;
  experiencePercentile?: number;
  percentileLabel?: string;
  heatLevel?: ExperienceHeatLevel;
  heatOpacity?: number;
  radiusMiles?: number;
}>;

export type MissionHeatmapMapCenterInput = Readonly<{
  latitude: number;
  longitude: number;
  displayLabel: string;
}>;

export type MissionHeatmapIndustryInput = Readonly<{
  industry: string;
  missionCount: number;
}>;

export type MissionHeatmapHistoryInput = Readonly<{
  industry: string;
  activityLabel?: string;
  cityLabel?: string;
}>;

export type MissionHeatmapMetricsInput = Readonly<{
  missionsVerified: number;
  missionsReported: number;
}>;

export type MissionHeatmapProjectionInput = Readonly<{
  renderable?: boolean;
  metrics: MissionHeatmapMetricsInput;
  heatmapBuckets?: readonly MissionHeatmapBucketInput[];
  heatmapLocations?: readonly MissionHeatmapLocationInput[];
  industriesServed?: readonly MissionHeatmapIndustryInput[];
  missionHistory?: readonly MissionHeatmapHistoryInput[];
  topLocationLabel?: string;
  primaryOperatingMarketLabel?: string;
  marketsServedCount?: number;
  lastMissionLabel?: string;
  regionSubtitle?: string;
  mapCenter?: MissionHeatmapMapCenterInput;
}>;

export type MissionHeatmapSummaryStats = Readonly<{
  totalMissions: number;
  primaryOperatingMarket: string | null;
  marketsServed: number | null;
  lastMissionLabel: string | null;
}>;

export type MissionHeatmapTooltipContent = Readonly<{
  marketLabel: string;
  missionCountLabel: string;
  percentileLabel: string;
  portfolioPercentageLabel: string;
}>;

const heatmapBucketKeyPattern = /^\d{4}-\d{2}$/;

function isLegacyMissionActivityDescriptionLabel(label: string): boolean {
  return /verified mission|reported mission|mission activity/i.test(label);
}

function resolveLastMissionDisplayLabel(
  projection: MissionHeatmapProjectionInput,
): string | null {
  const latestMission = projection.missionHistory?.[0];
  const historyCityLabel = latestMission?.cityLabel?.trim();
  if (historyCityLabel) {
    return historyCityLabel;
  }

  const storedLabel = projection.lastMissionLabel?.trim();
  if (storedLabel && !isLegacyMissionActivityDescriptionLabel(storedLabel)) {
    return storedLabel;
  }

  return latestMission?.industry?.trim() || null;
}

export function formatMissionHeatmapBucketLabel(bucketKey: string): string {
  if (!heatmapBucketKeyPattern.test(bucketKey)) {
    return bucketKey;
  }
  const [yearText, monthText] = bucketKey.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return bucketKey;
  }
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

export function buildMissionHeatmapRegionSubtitle(
  locationDisplay?: string | null,
  surroundingAreaSuffix = "& surrounding area",
  regionSubtitle?: string | null,
): string | undefined {
  if (regionSubtitle?.trim()) {
    return regionSubtitle.trim();
  }
  const normalized = locationDisplay?.trim();
  if (!normalized) {
    return undefined;
  }
  return `${normalized} ${surroundingAreaSuffix}`.trim();
}

export function resolveMissionSummaryIndustries(
  projection?: MissionHeatmapProjectionInput,
  industries?: readonly MissionHeatmapIndustryInput[],
): readonly MissionHeatmapIndustryInput[] {
  return industries ?? projection?.industriesServed ?? [];
}

export type IndustriesServedDisplay = Readonly<{
  count: number;
  industryLabels: readonly string[];
  industryListLabel: string;
}>;

export function buildIndustriesServedDisplay(
  projection?: MissionHeatmapProjectionInput,
  industries?: readonly MissionHeatmapIndustryInput[],
): IndustriesServedDisplay {
  const seenLabels = new Set<string>();
  const industryLabels = resolveMissionSummaryIndustries(projection, industries)
    .map((entry) => entry.industry.trim())
    .filter((label) => {
      if (!label) return false;
      const normalized = label.toLocaleLowerCase();
      if (seenLabels.has(normalized)) return false;
      seenLabels.add(normalized);
      return true;
    });
  return {
    count: industryLabels.length,
    industryLabels,
    industryListLabel: industryLabels.join(", "),
  };
}

export function buildMissionHeatmapSummaryStats(
  projection: MissionHeatmapProjectionInput,
): MissionHeatmapSummaryStats {
  const totalMissions = Math.max(projection.metrics.missionsReported, projection.metrics.missionsVerified);
  const primaryOperatingMarket = projection.primaryOperatingMarketLabel?.trim()
    || projection.topLocationLabel?.trim()
    || projection.heatmapLocations?.find((location) => location.heatLevel === "top_10")?.cityLabel?.trim()
    || projection.heatmapLocations?.[0]?.cityLabel?.trim()
    || null;
  const marketsServed = projection.marketsServedCount ?? projection.heatmapLocations?.length ?? null;
  const lastMissionLabel = resolveLastMissionDisplayLabel(projection);

  return {
    totalMissions,
    primaryOperatingMarket,
    marketsServed: marketsServed && marketsServed > 0 ? marketsServed : null,
    lastMissionLabel,
  };
}

export function resolveMissionHeatmapPortfolioPercentage(
  location: MissionHeatmapLocationInput,
  totalMissions?: number,
): number {
  if (
    typeof location.portfolioPercentage === "number"
    && (
      location.portfolioPercentage > 0
      || location.missionCount <= 0
      || !totalMissions
      || totalMissions <= 0
    )
  ) {
    return location.portfolioPercentage;
  }
  if (!totalMissions || totalMissions <= 0) {
    return 0;
  }
  return Math.round((location.missionCount / totalMissions) * 1000) / 10;
}

export function buildMissionHeatmapTooltipContent(
  location: MissionHeatmapLocationInput,
  labels: Readonly<{
    missionCount: (count: number) => string;
    portfolioPercentage: (percentage: number) => string;
    operatingAreasSuffix: string;
  }>,
  totalMissions?: number,
): MissionHeatmapTooltipContent {
  const portfolioPercentage = resolveMissionHeatmapPortfolioPercentage(location, totalMissions);
  const percentileLabel = location.percentileLabel?.trim()
    ? `${location.percentileLabel} ${labels.operatingAreasSuffix}`.trim()
    : labels.operatingAreasSuffix;

  return {
    marketLabel: location.cityLabel,
    missionCountLabel: labels.missionCount(location.missionCount),
    percentileLabel,
    portfolioPercentageLabel: labels.portfolioPercentage(portfolioPercentage),
  };
}

export function hasMissionHeatmapRenderableData(projection: MissionHeatmapProjectionInput): boolean {
  return buildMissionHeatmapSummaryStats(projection).totalMissions > 0
    || (projection.heatmapLocations?.length ?? 0) > 0;
}

export function resolveMissionHeatmapMapCenter(
  projection: MissionHeatmapProjectionInput,
  profileLocationCenter?: MissionHeatmapMapCenterInput | null,
): MissionHeatmapMapCenterInput | null {
  if (projection.mapCenter) {
    return projection.mapCenter;
  }
  const primaryLocation = projection.heatmapLocations?.[0];
  if (primaryLocation) {
    return {
      latitude: primaryLocation.latitude,
      longitude: primaryLocation.longitude,
      displayLabel: primaryLocation.cityLabel,
    };
  }
  if (profileLocationCenter) {
    return profileLocationCenter;
  }
  return null;
}

export function isMissionHeatmapTileVisible(
  projection: MissionHeatmapProjectionInput | null | undefined,
  profileLocationCenter?: MissionHeatmapMapCenterInput | null,
): boolean {
  if (isPublicProfileSectionRenderable(projection)) {
    return true;
  }
  return resolveMissionHeatmapMapCenter(
    projection ?? emptyMissionHeatmapProjection,
    profileLocationCenter,
  ) !== null;
}

const emptyMissionHeatmapProjection: MissionHeatmapProjectionInput = {
  metrics: {
    missionsVerified: 0,
    missionsReported: 0,
  },
};

export const experienceHeatColors: Readonly<Record<ExperienceHeatLevel, string>> = {
  top_10: "#dc2626",
  top_25: "#f97316",
  top_50: "#22c55e",
  bottom_50: "#3b82f6",
  none: "rgba(0, 0, 0, 0)",
};
